import React from 'react'
import moment from 'moment'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import store from '../../state/store'

const BADGE_LABELS = {
  priority: 'Priority',
  in_placement: 'In placement',
  in_proposed: 'Tutor selection',
  tutor_selected: 'Tutor selected',
  paid: 'Paid',
  deactivated: 'Deactivated',
  handed_over: 'Handed over',
  lp_deactivated: 'LP closed',
  converted: 'Converted',
  scheduled_call: 'Call booked',
}

const BADGE_VARIANTS = {
  priority: 'destructive',
  in_placement: 'warning',
  in_proposed: 'warning',
  tutor_selected: 'success',
  paid: 'success',
  deactivated: 'secondary',
  handed_over: 'info',
  lp_deactivated: 'secondary',
  converted: 'success',
  scheduled_call: 'info',
}

const StatusBadge = ({ kind, label }) => (
  <Badge variant={BADGE_VARIANTS[kind] || 'secondary'}>
    {label || BADGE_LABELS[kind] || kind}
  </Badge>
)

// Profile picture + short name, matching the manager/coordinator selectors on
// the enquiry header.
const ManagerCell = ({ manager, empty }) => {
  if (!manager) {
    return <em>{empty}</em>
  }

  const name = manager.short_name || manager.name

  return (
    <span className="flex items-center gap-2">
      <Avatar size="sm">
        <AvatarImage src={manager.profile_picture_url}/>
        <AvatarFallback>{(name || '?').charAt(0)}</AvatarFallback>
      </Avatar>
      {name}
    </span>
  )
}

class PipelineTable extends React.Component {
  onRowClick = (row) => {
    store.emit('pipeline:row:open', row)
  }

  renderRow = (row) => {
    const learners = (row.learners && row.learners.length) ? row.learners.join(', ') : '—'
    const medium = row.medium === 'local' ? 'f2f' : row.medium
    const due = row.due_at ? moment(row.due_at).format('YYYY-MM-DD') : '—'

    const rowClass =
      'cursor-pointer ' +
      (row.in_backlog ? 'shadow-[inset_3px_0_0_var(--primary)]' : '')

    return (
      <tr key={row.journey_key} className={rowClass} onClick={() => this.onRowClick(row)}>
        <td>
          <div className="flex items-center gap-2.5">
            {row.customer_image ? (
              <img
                src={row.customer_image}
                alt=""
                className="size-8 rounded-full object-cover"
              />
            ) : null}
            <div>
              <div className="font-medium text-foreground">{row.customer_name || '—'}</div>
              <div className="text-[11px] text-muted-foreground">
                {row.source_type === 'Sales::Lead' ? 'Lead' : `Enquiry #${row.source_id}`}
              </div>
            </div>
          </div>
        </td>
        <td>
          {row.lesson_plan_id ? (
            <div>
              <div>LP {row.lesson_plan_reference || `#${row.lesson_plan_id}`}</div>
              {row.subject || medium ? (
                <div className="max-w-48 whitespace-normal break-words text-[11px] text-muted-foreground">
                  {[row.subject, medium].filter(Boolean).join(' · ')}
                </div>
              ) : null}
            </div>
          ) : '—'}
        </td>
        <td>{learners}</td>
        <td><ManagerCell manager={row.manager} empty="unclaimed" /></td>
        <td><ManagerCell manager={row.coordinator} empty="—" /></td>
        <td>{due}</td>
        <td>
          <div className="flex flex-wrap gap-1">
            {row.badges && row.badges.length
              ? row.badges.map((b) => (
                  <StatusBadge
                    key={b}
                    kind={b}
                    label={b === 'scheduled_call' && row.scheduled_call_at
                      ? `Call ${moment(row.scheduled_call_at).calendar()}`
                      : null}
                  />
                ))
              : null}
          </div>
        </td>
      </tr>
    )
  }

  render() {
    return (
      <table className="data-table data-table--compact data-table--clickable mb-0 text-[13px] [&_td]:align-middle">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Lesson plan</th>
            <th>Learner(s)</th>
            <th>LS</th>
            <th>SDR</th>
            <th>Due</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {this.props.rows.map(this.renderRow)}
        </tbody>
      </table>
    )
  }
}

export default PipelineTable
